import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  downloadCSV,
  extractValidSavedQueries,
  ListBase,
  useDataProvider,
  useGetIdentity,
  useListContext,
  useNotify,
  useSavedQueries,
  type SavedQuery,
} from "ra-core";
import jsonExport from "jsonexport/dist";
import { Link } from "react-router";
import {
  Check,
  Download,
  Filter,
  Globe,
  ListPlus,
  Map as MapIcon,
  Mail,
  Phone,
  Plus,
  Route as RouteIcon,
  Search,
  Table2,
  X,
} from "lucide-react";
import { useNavigate } from "react-router";
import { DirectoryMap } from "./DirectoryMap";
import { useInCrm } from "./useInCrm";
import { useIsDueno } from "../useIsDueno";
import { MAP_POINT_LIMIT } from "./directoryMapData";
import { ListPagination } from "@/components/admin/list-pagination";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { MobilePage } from "../MobilePage";
import { NoZoneNotice } from "../freelancerNotices";
import { OfferDot } from "../ui";
import { offerColor, useOffers } from "../useOffers";
import { useSequences, type Sequence } from "../sequences/enroll";
import { ESTADOS, RATINGS, SECTORES } from "./directoryFilters";
import { FiltersPanel, FUENTES, useF } from "./FiltersPanel";
import { ResearchButton } from "../ai/ai";
import { ReviewsButton, useReviewThemes } from "../reviews/reviews";
import { PromoteButton } from "./PromoteButton";
import { promoteMany, promoteMessage } from "./promote";
import { RoundStart } from "./RoundStart";
import { ThermoBar, TH_LEVELS } from "../thermo/thermo";
import { HelpTooltip } from "../help/HelpTooltip";

export type DirRow = {
  id: number;
  nombre_comercial: string | null;
  razon_social: string | null;
  actividad: string | null;
  sector: string | null;
  municipio: string | null;
  estado: string;
  rango_personal: string | null;
  tamano: string | null;
  telefono: string | null;
  email_tipo: string | null;
  sitio_web: string | null;
  fits: string[];
  priority: number;
  thermo_base: number;
  tier: string | null;
  company_id: number | null;
  google_rating: number | null;
  google_reviews: number | null;
  lat: number | null;
  lng: number | null;
};

/** Apagado por defecto (Bloque 4 parte 1); se recuerda por dispositivo. */
const VIEW_KEY = "directory.view";
type DirectoryView = "table" | "map";
const useDirectoryView = () => {
  const [view, setView] = useState<DirectoryView>(() => {
    try {
      return localStorage.getItem(VIEW_KEY) === "map" ? "map" : "table";
    } catch {
      return "table";
    }
  });
  const set = (v: DirectoryView) => {
    setView(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      // localStorage puede fallar (privado/bloqueado); el interruptor sigue
      // funcionando en memoria para esta sesión.
    }
  };
  return [view, set] as const;
};

// Vistas del prototipo (se suman a las que guardes tú)
const PRESETS: { label: string; filter: Record<string, unknown> }[] = [
  {
    label: "Storcky · Qro. nivel A",
    filter: { _ofertas: ["storcky"], _zona: ["Querétaro"], _tiers: ["A"] },
  },
  {
    label: "Sin sitio web, dominio propio",
    filter: { _dominio_sin_web: true },
  },
  {
    label: "Restaurantes Qro. 6+",
    filter: {
      _sectores: ["Restaurantes"],
      _estados: ["Querétaro"],
      _emp_min: "6",
    },
  },
  { label: "Recargadoras de extintores", filter: { _ofertas: ["kelmo"] } },
];

const EXPORT_MAX = 5000;

/** Directorio (PRD D.1 y D.2) sobre los 2.5 M del DENUE. */
export const DirectoryExplorer = () => (
  <ListBase
    resource="directory"
    perPage={25}
    sort={{ field: "thermo_base", order: "DESC" }}
    storeKey="directory.termometro"
    disableSyncWithLocation={false}
  >
    <MobilePage title="Directorio">
      <Explorer />
    </MobilePage>
  </ListBase>
);

const Explorer = () => {
  const { total, isPending, data, selectedIds, filterValues, sort } =
    useListContext<DirRow>();
  const isMobile = useIsMobile();
  const [more, setMore] = useState(false);
  const [view, setView] = useDirectoryView();
  const activeCount = useActiveChips().length;
  const dataProvider = useDataProvider();
  const mapQuery = useQuery({
    queryKey: ["directory-map", filterValues, sort],
    enabled: view === "map",
    queryFn: async () => {
      const { data, total } = await dataProvider.getList<DirRow>("directory", {
        filter: filterValues,
        sort,
        pagination: { page: 1, perPage: MAP_POINT_LIMIT },
      });
      return { rows: data, total };
    },
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="max-md:hidden">
        <h1 className="text-2xl font-extrabold tracking-tight">Directorio</h1>
        <p className="text-sm text-muted-foreground">
          2.5 millones de empresas · DENUE corte 05/2026 · + las que agregues tú
          o la IA
        </p>
      </div>
      <NoZoneNotice />
      <Toolbar
        onMore={() => setMore(!more)}
        activeCount={activeCount}
        view={view}
        onViewChange={setView}
      />
      <Views />
      <div
        className={cn(
          "grid gap-4",
          more && !isMobile && "grid-cols-[290px_minmax(0,1fr)] items-start",
        )}
      >
        {more && !isMobile && (
          <div className="sticky top-16 max-h-[calc(100vh-5rem)] overflow-y-auto">
            <FiltersPanel />
          </div>
        )}
        <section className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-baseline gap-2">
            <b className="text-lg">
              {isPending ? "…" : (total ?? 0).toLocaleString("es-MX")}
            </b>
            <span className="text-sm text-muted-foreground">
              {total === 1 ? "empresa" : "empresas"}{" "}
              <span className="max-md:hidden">
                {isPending
                  ? ""
                  : "· ordenadas por termómetro (las más calientes primero)"}
              </span>
            </span>
          </div>
          <ActiveChips />
          {view === "map" ? (
            <MapView
              rows={mapQuery.data?.rows ?? []}
              total={mapQuery.data?.total ?? 0}
              isPending={mapQuery.isPending}
            />
          ) : (
            <>
              {isMobile ? (
                <Cards rows={data ?? []} />
              ) : (
                <Table rows={data ?? []} />
              )}
              <ListPagination rowsPerPageOptions={[25, 50, 100]} />
            </>
          )}
          <p className="text-xs text-muted-foreground">Fuente: INEGI (DENUE)</p>
        </section>
      </div>
      {isMobile && (
        <Sheet open={more} onOpenChange={setMore}>
          <SheetContent
            side="bottom"
            className="max-h-[88vh] gap-0 overflow-y-auto p-2"
          >
            <SheetTitle className="sr-only">Filtros</SheetTitle>
            <FiltersPanel onClose={() => setMore(false)} />
          </SheetContent>
        </Sheet>
      )}
      {!!selectedIds.length && <BulkBar />}
    </div>
  );
};

const Toolbar = ({
  onMore,
  activeCount,
  view,
  onViewChange,
}: {
  onMore: () => void;
  activeCount: number;
  view: "table" | "map";
  onViewChange: (v: "table" | "map") => void;
}) => {
  const { f, set } = useF();
  const { data: offers } = useOffers();
  const [q, setQ] = useState(f["nombre_comercial@ilike"] ?? "");
  useEffect(
    () => setQ(f["nombre_comercial@ilike"] ?? ""),
    [f["nombre_comercial@ilike"]],
  );
  useEffect(() => {
    const t = setTimeout(() => {
      if ((f["nombre_comercial@ilike"] ?? "") !== q)
        set({ "nombre_comercial@ilike": q.trim() || undefined });
    }, 400);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const single = (k: string) => (f[k]?.length === 1 ? f[k][0] : "");
  const sel =
    "h-9 rounded-lg border bg-card px-2 text-sm font-semibold max-w-52";
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="flex h-9 min-w-52 flex-1 items-center gap-2 rounded-lg border bg-card px-2.5 md:max-w-80">
        <Search className="size-4 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar empresa…"
          className="w-full bg-transparent text-sm outline-none"
          aria-label="Buscar empresa"
        />
      </label>
      <select
        aria-label="Oferta"
        className={sel}
        value={single("_ofertas")}
        onChange={(e) =>
          set({
            _ofertas: e.target.value ? [e.target.value] : undefined,
            "fits@cs": undefined,
          })
        }
      >
        <option value="">Todas las ofertas</option>
        {offers?.map((o) => (
          <option key={o.id} value={o.slug}>
            {o.name}
          </option>
        ))}
      </select>
      <select
        aria-label="Estado"
        className={sel}
        value={single("_estados")}
        onChange={(e) =>
          set({
            _estados: e.target.value ? [e.target.value] : undefined,
            estado: undefined,
            municipio: undefined,
          })
        }
      >
        <option value="">Todos los estados</option>
        {ESTADOS.map((x) => (
          <option key={x}>{x}</option>
        ))}
      </select>
      <select
        aria-label="Sector"
        className={sel}
        value={single("_sectores")}
        onChange={(e) =>
          set({
            _sectores: e.target.value ? [e.target.value] : undefined,
            sector: undefined,
          })
        }
      >
        <option value="">Todos los sectores</option>
        {SECTORES.map((x) => (
          <option key={x}>{x}</option>
        ))}
      </select>
      <select
        aria-label="Termómetro"
        className={sel}
        value={f._termo ?? ""}
        onChange={(e) => set({ _termo: e.target.value || undefined })}
      >
        <option value="">Todo el termómetro</option>
        {TH_LEVELS.map(([min, label]) => (
          <option key={min} value={min}>
            {label}
          </option>
        ))}
      </select>
      <Button variant="outline" onClick={onMore}>
        <Filter />
        Más filtros {activeCount ? `(${activeCount})` : ""}
      </Button>
      <Button
        variant="outline"
        onClick={() => onViewChange(view === "map" ? "table" : "map")}
      >
        {view === "map" ? (
          <>
            <Table2 /> Ver tabla
          </>
        ) : (
          <>
            <MapIcon /> Ver mapa
          </>
        )}
      </Button>
      <RoundStart />
      <ExportButton />
    </div>
  );
};

/** Vistas: las del prototipo + las que guardes (búsquedas guardadas). */
const Views = () => {
  const { f, sort, perPage, displayedFilters, setFilters, setSort } = {
    ...useF(),
    ...useListContext(),
  };
  const [saved, setSaved] = useSavedQueries("directory");
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const mine = extractValidSavedQueries(saved);
  const apply = (
    filter: Record<string, unknown>,
    s?: SavedQuery["value"]["sort"],
  ) => {
    setFilters(filter, displayedFilters);
    if (s) setSort(s);
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {PRESETS.map((p) => (
        <button
          key={p.label}
          className="rounded-full border bg-card px-2.5 py-0.5 text-xs font-semibold hover:bg-secondary"
          onClick={() => apply(p.filter)}
        >
          ★ {p.label}
        </button>
      ))}
      {mine.map((q, i) => (
        <span
          key={i}
          className="flex items-center gap-1 rounded-full border bg-card pl-2.5 pr-1 text-xs font-semibold"
        >
          <button onClick={() => apply(q.value.filter, q.value.sort)}>
            ★ {q.label}
          </button>
          <button
            aria-label={`Quitar vista ${q.label}`}
            className="grid size-5 place-items-center text-muted-foreground hover:text-foreground"
            onClick={() => setSaved(mine.filter((_, j) => j !== i))}
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <button
        className="flex items-center gap-1 rounded-full border border-dashed px-2.5 py-0.5 text-xs font-semibold"
        onClick={() => setNaming(true)}
      >
        <Plus className="size-3" /> Guardar vista
      </button>
      <Dialog open={naming} onOpenChange={setNaming}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Guardar vista</DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nombre de la vista"
          />
          <DialogFooter>
            <Button
              disabled={!name.trim()}
              onClick={() => {
                setSaved([
                  ...mine,
                  {
                    label: name.trim(),
                    value: { filter: f, sort, perPage, displayedFilters },
                  },
                ]);
                setName("");
                setNaming(false);
              }}
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** Chips de los filtros activos, cada uno con su X. */
const useActiveChips = () => {
  const { f, set } = useF();
  const { data: offers } = useOffers();
  const { data: themes } = useReviewThemes();
  const chips: { label: string; remove: () => void }[] = [];
  const arr = (k: string, fmt: (v: string) => string) =>
    (f[k] ?? []).forEach((v: string) =>
      chips.push({
        label: fmt(v),
        remove: () => set({ [k]: f[k].filter((x: string) => x !== v) }),
      }),
    );
  const one = (k: string, label: string | ((v: any) => string)) => {
    if (f[k] === undefined || f[k] === "") return;
    chips.push({
      label: typeof label === "string" ? label : label(f[k]),
      remove: () => set({ [k]: undefined }),
    });
  };
  one("nombre_comercial@ilike", (v) => `Nombre: ${v}`);
  arr(
    "_ofertas",
    (v) => `Oferta: ${offers?.find((o) => o.slug === v)?.name ?? v}`,
  );
  one("fits@cs", (v) => `Oferta: ${String(v).replace(/[{}]/g, "")}`);
  arr("_zona", (v) => `Zona: ${v}`);
  arr("_estados", (v) => v);
  one("estado", (v) => v);
  one("municipio", (v) => `Municipio: ${v}`);
  one("municipio@ilike", (v) => `Municipio: ${v}`);
  one("_cp", (v) => `C.P. ${v}${f._radio_km ? ` + ${f._radio_km} km` : ""}`);
  arr("_sectores", (v) => v);
  one("sector", (v) => v);
  one("_scian", (v) => `SCIAN ${v}…`);
  arr("_tamanos", (v) => v);
  one("tamano", (v) => v);
  if (f._emp_min || f._emp_max)
    chips.push({
      label: `Empleados ${f._emp_min || 0}–${f._emp_max || "∞"}`,
      remove: () => set({ _emp_min: undefined, _emp_max: undefined }),
    });
  arr("_tiers", (v) => `Nivel ${v}`);
  one("tier", (v) => `Nivel ${v}`);
  one("_prio_min", (v) => `Prioridad ≥ ${v}`);
  one(
    "_termo",
    (v) =>
      `Termómetro: ${TH_LEVELS.find(([m]) => String(m) === String(v))?.[1]}`,
  );
  one("_tel", "Con teléfono");
  one("_email", "Con correo");
  one("_web", "Con sitio web");
  one("_dominio_sin_web", "Dominio propio sin sitio");
  one("email_tipo", (v) => `Correo ${String(v).replace("_", " ")}`);
  if (f._alta_desde || f._alta_hasta)
    chips.push({
      label: `Alta ${f._alta_desde || "…"}–${f._alta_hasta || "…"}`,
      remove: () => set({ _alta_desde: undefined, _alta_hasta: undefined }),
    });
  one("company_id@is", (v) =>
    v === "null" ? "No está en el CRM" : "Ya en el CRM",
  );
  one("researched_at@is", (v) =>
    v === "null" ? "Sin investigar" : "Investigadas con IA",
  );
  if (f._ocultar_dnc === false)
    chips.push({
      label: 'Incluye "No contactar"',
      remove: () => set({ _ocultar_dnc: undefined }),
    });
  arr("_fuentes", (v) => `Fuente: ${FUENTES.find(([x]) => x === v)?.[1] ?? v}`);
  one("_resenas", "Con reseñas analizadas");
  arr("_quejas", (v) => `Se quejan de: ${themes?.get(v)?.label ?? v}`);
  one("_rating", (v) => RATINGS.find(([x]) => x === v)?.[1] ?? v);
  return chips;
};

const ActiveChips = () => {
  const chips = useActiveChips();
  const { set, f } = useF();
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {chips.map((c) => (
        <span
          key={c.label}
          className="flex items-center gap-1 rounded-full border bg-card py-0.5 pl-2.5 pr-1 text-xs font-semibold"
        >
          {c.label}
          <button
            aria-label={`Quitar filtro ${c.label}`}
            onClick={c.remove}
            className="grid size-5 place-items-center text-muted-foreground hover:text-foreground"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <button
        className="text-xs font-semibold text-muted-foreground underline"
        onClick={() =>
          set(Object.fromEntries(Object.keys(f).map((k) => [k, undefined])))
        }
      >
        Quitar todos
      </button>
    </div>
  );
};

const ContactIcons = ({ r }: { r: DirRow }) => (
  <span className="flex gap-1.5 text-[var(--faint)]">
    <span
      className={cn(r.telefono && "text-foreground")}
      title={r.telefono ? "Tiene teléfono" : "Sin teléfono"}
    >
      <Phone className="size-4" />
    </span>
    <span
      className={cn(r.email_tipo !== "sin_email" && "text-foreground")}
      title={`Correo ${String(r.email_tipo ?? "").replace("_", " ")}`}
    >
      <Mail className="size-4" />
    </span>
    <span
      className={cn(r.sitio_web && "text-foreground")}
      title={r.sitio_web ? "Tiene sitio" : "Sin sitio"}
    >
      <Globe className="size-4" />
    </span>
  </span>
);

const Fits = ({ r }: { r: DirRow }) => {
  const { data: offers } = useOffers();
  return (
    <div className="flex flex-wrap gap-1">
      {r.fits.map((s) => (
        <span
          key={s}
          className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap"
        >
          <OfferDot color={offerColor({ slug: s })} />
          {offers?.find((o) => o.slug === s)?.name ?? s}
        </span>
      ))}
    </div>
  );
};

const emp = (r: DirRow) => r.rango_personal?.replace(" personas", "") ?? "";
const name = (r: DirRow) =>
  r.nombre_comercial ?? r.razon_social ?? "Sin nombre";

const Table = ({ rows }: { rows: DirRow[] }) => {
  const { selectedIds, onToggleItem, onSelect } = useListContext();
  const inCrm = useInCrm(rows);
  const isDueno = useIsDueno(); // Bloque 0b: Google rating (SerpApi) is owner-only
  const allOn =
    rows.length > 0 && rows.every((r) => selectedIds.includes(r.id));
  return (
    <div className="overflow-x-auto rounded-lg border bg-card">
      <table className="w-full min-w-[960px] text-sm">
        <thead>
          <tr className="border-b text-left text-xs uppercase tracking-wide text-[var(--faint)]">
            <th className="w-9 px-3 py-2">
              <input
                type="checkbox"
                aria-label="Seleccionar la página"
                className="size-4 accent-[var(--primary)]"
                checked={allOn}
                onChange={() =>
                  onSelect(
                    allOn
                      ? selectedIds.filter(
                          (id) => !rows.some((r) => r.id === id),
                        )
                      : [
                          ...new Set([
                            ...selectedIds,
                            ...rows.map((r) => r.id),
                          ]),
                        ],
                  )
                }
              />
            </th>
            {[
              "Empresa",
              "Termómetro",
              "Ubicación",
              "Giro",
              "Empleados",
              "Contacto",
              "Google",
              "Encaja en",
              "CRM",
            ]
              .filter((h) => isDueno || h !== "Google")
              .map((h) => (
                <th key={h} className="px-2.5 py-2">
                  <span className="inline-flex items-center gap-1">
                    {h}
                    {h === "Termómetro" && (
                      <HelpTooltip topic="company.thermo" />
                    )}
                    {h === "Encaja en" && (
                      <HelpTooltip topic="directory.fits" />
                    )}
                  </span>
                </th>
              ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b hover:bg-secondary/60">
              <td className="px-2.5 py-2">
                <input
                  type="checkbox"
                  aria-label={`Seleccionar ${name(r)}`}
                  className="size-4 accent-[var(--primary)]"
                  checked={selectedIds.includes(r.id)}
                  onChange={() => onToggleItem(r.id)}
                />
              </td>
              <td className="max-w-64 px-3 py-2">
                <Link
                  to={`/directory/${r.id}/show`}
                  className="font-bold hover:text-primary hover:underline"
                >
                  {name(r)}
                </Link>
                <div className="truncate text-xs text-muted-foreground">
                  {r.actividad}
                </div>
              </td>
              <td className="px-2.5 py-2 whitespace-nowrap">
                <ThermoBar value={r.thermo_base} mini />
              </td>
              <td className="px-2.5 py-2 whitespace-nowrap">
                {r.municipio}
                <div className="text-xs text-muted-foreground">{r.estado}</div>
              </td>
              <td className="px-2.5 py-2">{r.sector}</td>
              <td className="px-2.5 py-2 whitespace-nowrap">{emp(r)}</td>
              <td className="px-2.5 py-2">
                <ContactIcons r={r} />
              </td>
              {isDueno && (
                <td className="px-2.5 py-2 whitespace-nowrap">
                  {r.google_rating ? (
                    <>
                      {r.google_rating} ★
                      <div className="text-xs text-muted-foreground">
                        {r.google_reviews} reseñas
                      </div>
                    </>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
              )}
              <td className="px-2.5 py-2">
                <Fits r={r} />
              </td>
              <td className="px-2.5 py-2">
                {inCrm(r) ? (
                  <Link
                    to={`/companies/${r.company_id}/show`}
                    className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold"
                  >
                    <Check className="size-3" /> En CRM
                  </Link>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && (
        <p className="p-6 text-center text-muted-foreground">
          Ninguna empresa coincide con los filtros. Quita alguno para ver más
          resultados.
        </p>
      )}
    </div>
  );
};

/** Mapa (Bloque 4 parte 1): usa lat/lng existentes, limitado a `MAP_POINT_LIMIT`. */
const MapView = ({
  rows,
  total,
  isPending,
}: {
  rows: DirRow[];
  total: number;
  isPending: boolean;
}) => {
  if (isPending)
    return (
      <p className="py-6 text-center text-muted-foreground">
        Cargando el mapa…
      </p>
    );
  if (!rows.length)
    return (
      <p className="py-6 text-center text-muted-foreground">
        Ninguna empresa coincide con los filtros. Quita alguno para ver más
        resultados.
      </p>
    );
  return (
    <div className="flex flex-col gap-2">
      {total > rows.length && (
        <p className="text-xs text-muted-foreground">
          Mostrando {rows.length.toLocaleString("es-MX")} de{" "}
          {total.toLocaleString("es-MX")} empresas (las más calientes primero).
          Ajusta los filtros para ver otras.
        </p>
      )}
      <DirectoryMap
        rows={rows.map((r) => ({
          id: r.id,
          lat: r.lat,
          lng: r.lng,
          nombre: name(r),
          thermo_base: r.thermo_base,
        }))}
      />
    </div>
  );
};

const Cards = ({ rows }: { rows: DirRow[] }) => {
  const { selectedIds, onToggleItem } = useListContext();
  const inCrm = useInCrm(rows);
  const isDueno = useIsDueno();
  if (!rows.length)
    return (
      <p className="py-6 text-center text-muted-foreground">
        Ninguna empresa coincide con los filtros. Quita alguno para ver más
        resultados.
      </p>
    );
  return (
    <div className="flex flex-col gap-2">
      {rows.map((r) => {
        const on = selectedIds.includes(r.id);
        return (
          <Card
            key={r.id}
            className={cn("gap-1.5 p-3", on && "border-primary")}
          >
            <div className="flex items-start gap-2.5">
              <input
                type="checkbox"
                aria-label={`Seleccionar ${name(r)}`}
                className="mt-1 size-5 accent-[var(--primary)]"
                checked={on}
                onChange={() => onToggleItem(r.id)}
              />
              <div className="min-w-0 flex-1">
                <Link
                  to={`/directory/${r.id}/show`}
                  className="block font-bold"
                >
                  {name(r)}
                </Link>
                <div className="truncate text-xs text-muted-foreground">
                  {r.actividad}
                </div>
                <div className="text-xs text-muted-foreground">
                  {[r.municipio, r.estado].filter(Boolean).join(", ")} ·{" "}
                  {emp(r)}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <ThermoBar value={r.thermo_base} mini />
                {inCrm(r) && (
                  <span className="rounded-full border px-2 py-0.5 text-[11px] font-semibold">
                    En CRM
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 pl-7">
              <Fits r={r} />
              <span className="flex items-center gap-2">
                {isDueno && r.google_rating != null && (
                  <span className="text-xs font-semibold whitespace-nowrap">
                    {r.google_rating} ★
                  </span>
                )}
                <ContactIcons r={r} />
              </span>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

/** Acciones masivas (PRD D.2). */
const BulkBar = () => {
  const { selectedIds, onUnselectItems } = useListContext();
  const isDueno = useIsDueno();
  return (
    <div
      className="fixed left-1/2 z-40 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 max-md:w-[calc(100vw-2rem)] flex-wrap items-center gap-2 rounded-xl bg-foreground px-3 py-2.5 text-background shadow-xl max-md:bottom-20 md:bottom-4"
      role="region"
      aria-label="Acciones"
    >
      <b className="px-1">
        {selectedIds.length} seleccionada{selectedIds.length > 1 ? "s" : ""}
      </b>
      <RouteButton />
      <PromoteButton />
      <CadenceBulkButton />
      <ResearchButton
        ids={selectedIds as number[]}
        onDone={() => onUnselectItems()}
      />
      {isDueno && (
        <ReviewsButton
          ids={selectedIds as number[]}
          onDone={() => onUnselectItems()}
        />
      )}
      <Button
        size="icon"
        variant="ghost"
        className="size-8 text-background"
        aria-label="Quitar selección"
        onClick={() => onUnselectItems()}
      >
        <X />
      </Button>
    </div>
  );
};

/** Arma la ruta (Fase 2) con la selección actual: trae lat/lng y pasa a /directory/ruta por router state. */
const RouteButton = () => {
  const { selectedIds } = useListContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await dataProvider.getMany<DirRow>("directory", {
        ids: selectedIds,
      });
      navigate("/directory/ruta", {
        state: {
          stops: data.map((r) => ({
            id: r.id,
            lat: r.lat,
            lng: r.lng,
            nombre: name(r),
            thermo_base: r.thermo_base,
            company_id: r.company_id,
          })),
        },
      });
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    }
    setBusy(false);
  };
  return (
    <Button
      size="sm"
      variant="outline"
      className="border-background/40 bg-transparent text-background"
      disabled={busy}
      onClick={run}
    >
      <RouteIcon />
      {busy ? "Armando…" : "Armar ruta"}
    </Button>
  );
};

/** + Cadencia: pasa al CRM en el producto de la cadencia (si falta) e inscribe. */
const CadenceBulkButton = () => {
  const { selectedIds, onUnselectItems, refetch } = useListContext();
  const { data: sequences } = useSequences();
  const { data: offers } = useOffers();
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async (s: Sequence) => {
    const offer = offers?.find((o) => o.id === s.offer_id);
    if (!offer) return;
    setBusy(true);
    const r = await promoteMany(selectedIds, offer, s, Number(identity?.id));
    setBusy(false);
    setOpen(false);
    onUnselectItems();
    refetch();
    const [msg, type] = promoteMessage(r, offer.name, s.name);
    notify(msg, { type });
  };
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="border-background/40 bg-transparent text-background"
        onClick={() => setOpen(true)}
      >
        <ListPlus />
        Cadencia
      </Button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Inscribir en cadencia</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Las que aún no están en el CRM pasan en el producto de la cadencia.
            Las de No contactar se omiten.
          </p>
          <div className="grid gap-2">
            {sequences
              ?.filter((s) => s.offer_id)
              .map((s) => (
                <Button
                  key={s.id}
                  variant="outline"
                  className="h-12"
                  disabled={busy}
                  onClick={() => run(s)}
                >
                  {s.name}
                </Button>
              ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

/** Exportar CSV con los filtros actuales (máx. 5,000 filas). Solo el dueño: los términos de uso prohíben a los
 * freelancers exportar el directorio (la base también se lo niega). */
const ExportButton = () => (useIsDueno() ? <ExportDirectory /> : null);

const ExportDirectory = () => {
  const { filterValues, sort, total } = useListContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    const rows: DirRow[] = [];
    // PostgREST entrega máximo 1,000 por consulta
    for (let page = 1; rows.length < EXPORT_MAX; page++) {
      const { data } = await dataProvider.getList<
        DirRow & Record<string, unknown>
      >("directory", {
        filter: filterValues,
        sort,
        pagination: { page, perPage: 1000 },
      });
      rows.push(...data);
      if (data.length < 1000) break;
    }
    const out = rows.slice(0, EXPORT_MAX).map((r: any) => ({
      nombre: r.nombre_comercial,
      razon_social: r.razon_social,
      actividad: r.actividad,
      sector: r.sector,
      municipio: r.municipio,
      estado: r.estado,
      cp: r.cp,
      empleados: r.rango_personal,
      telefono: r.telefono,
      correo: r.email,
      tipo_correo: r.email_tipo,
      sitio_web: r.sitio_web,
      encaja_en: r.fits.join(" "),
      termometro: r.thermo_base,
      prioridad: r.priority,
      nivel: r.tier,
      en_crm: r.company_id ? "sí" : "no",
    }));
    jsonExport(out, (err: Error | null, csv: string) => {
      setBusy(false);
      if (err) return notify(err.message, { type: "error" });
      downloadCSV(csv, "directorio");
      notify(
        `${out.length.toLocaleString("es-MX")} empresas exportadas` +
          ((total ?? 0) > EXPORT_MAX
            ? ` (máximo ${EXPORT_MAX.toLocaleString("es-MX")})`
            : ""),
        { type: "success" },
      );
    });
  };
  return (
    <Button variant="outline" onClick={run} disabled={busy}>
      <Download />
      {busy ? "Exportando…" : "Exportar CSV"}
    </Button>
  );
};
